import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { getPatientSummaryFullName } from '@/api/mappers';
import { SensitiveRecordBadge } from '@/components/patient/SensitiveRecordBadge';
import { cn } from '@/lib/utils';
import {
  DataTableHeaderRow,
  DataTablePill,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
  type SortDirection,
} from '@/components/ui/data-table';
import type { PatientSortKey } from '@/features/patient-search/lib/sortPatientSummaries';
import { formatDate, formatPartialDate } from '@/lib/format';
import type { PatientSummaryViewModel } from '@/models/patient';

/**
 * Patient rows + header cells for the browse list. Sits under `DataTable` +
 * `DataTableToolbar` as the `DataTableTable` layer.
 */
export function BrowsePatientsTableBody({
  patients,
  emptyCell,
  sortKey,
  sortDir,
  onSortChange,
}: {
  readonly patients: readonly PatientSummaryViewModel[];
  readonly emptyCell: string;
  readonly sortKey: PatientSortKey | null;
  readonly sortDir: SortDirection;
  readonly onSortChange: (key: PatientSortKey) => void;
}): ReactElement {
  const { t } = useTranslation();

  const sortProps = (
    key: PatientSortKey,
  ): { readonly sortDirection: SortDirection | null; readonly onSort: () => void } => ({
    sortDirection: sortKey === key ? sortDir : null,
    onSort: () => {
      onSortChange(key);
    },
  });

  return (
    <DataTableTable aria-label={t('routes.browsePatients.listAriaLabel')}>
      <DataTableThead>
        <DataTableHeaderRow>
          <DataTableTh widthClassName="w-[22%] min-w-[7rem]" sortable {...sortProps('name')}>
            {t('routes.browsePatients.table.patientName')}
          </DataTableTh>
          <DataTableTh className="tabular-nums" widthClassName="w-28">
            {t('routes.browsePatients.table.mrn')}
          </DataTableTh>
          <DataTableTh
            className="whitespace-nowrap"
            widthClassName="w-32"
            sortable
            {...sortProps('dob')}
          >
            {t('routes.browsePatients.table.dob')}
          </DataTableTh>
          <DataTableTh className="w-20" sortable {...sortProps('sex')}>
            {t('routes.browsePatients.table.sex')}
          </DataTableTh>
          <DataTableTh
            className="min-w-[7rem] max-w-[10rem] md:min-w-32"
            sortable
            {...sortProps('program')}
          >
            {t('routes.browsePatients.table.program')}
          </DataTableTh>
          <DataTableTh
            className="min-w-0"
            widthClassName="min-w-24"
            sortable
            {...sortProps('gc')}
          >
            {t('routes.browsePatients.table.assignedGc')}
          </DataTableTh>
          <DataTableTh
            className="w-32"
            widthClassName="w-32"
            align="right"
            sortable
            {...sortProps('lastModified')}
          >
            {t('routes.browsePatients.table.lastModified')}
          </DataTableTh>
        </DataTableHeaderRow>
      </DataTableThead>
      <DataTableTbody>
        {patients.map((p) => {
          const name = getPatientSummaryFullName(p) ?? t('patient.header.unknownName');
          return (
            <DataTableTr
              key={p.guid}
              className={cn(
                p.sensitive &&
                  'bg-destructive/[0.03] [&>td:first-child]:border-l-2 [&>td:first-child]:border-l-destructive',
              )}
            >
              <DataTableTd className="pr-1 font-medium text-foreground" dataOffset={false}>
                <span className="flex items-center gap-2 min-w-0">
                  <Link
                    to={`/patients/${p.guid}`}
                    className="truncate text-primary hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  >
                    {name}
                  </Link>
                  {p.sensitive && <SensitiveRecordBadge />}
                </span>
              </DataTableTd>
              <DataTableTd className="font-normal tabular-nums text-foreground/90" dataOffset>
                {p.primaryIdentifier !== null ? p.primaryIdentifier : emptyCell}
              </DataTableTd>
              <DataTableTd className="whitespace-nowrap text-foreground/90" dataOffset>
                {formatPartialDate(p.dateOfBirth)}
              </DataTableTd>
              <DataTableTd className="text-foreground/90">
                {p.sex !== null ? t(`patient.sex.${p.sex}`) : emptyCell}
              </DataTableTd>
              <DataTableTd>
                {p.program !== null ? (
                  <DataTablePill className="max-w-[14rem]">{p.program}</DataTablePill>
                ) : (
                  <span className="text-muted-foreground text-xs">{emptyCell}</span>
                )}
              </DataTableTd>
              <DataTableTd
                className="text-foreground/90"
                dataOffset
                title={p.assignedGc ?? undefined}
              >
                {p.assignedGc !== null && p.assignedGc.length > 0 ? p.assignedGc : emptyCell}
              </DataTableTd>
              <DataTableTd className="whitespace-nowrap text-foreground/90" dataOffset align="right">
                {formatDate(p.lastInteractedAt)}
              </DataTableTd>
            </DataTableTr>
          );
        })}
      </DataTableTbody>
    </DataTableTable>
  );
}
